import { useMemo } from "react";
import { CountVizDatum } from "../../types";
import { getPercentage, getVal } from "oolib";

export interface MultiLevelPieData {
  isMultiLevel: boolean;
  parentData: any[];
  childDataByParent: { [parentIndex: number]: any[] };
  // New fields for N-level support
  levelData: { [level: number]: any[] }; // Data organized by level
  maxLevel: number; // Maximum depth of nesting
  levelColors: { [level: number]: { [index: number]: string } }; // Colors by level and index
}

export interface LevelDataItem {
  labels: {
    count: number;
    percentage: number;
    name: string;
    tooltipLabel: string;
    level: number;
    parentPath: number[]; // Array of parent indices [level0Index, level1Index, ...]
    index: number; // Index within its level
  };
}

/**
 * Recursively process data at all levels
 */
const processLevelRecursive = (
  data: any[],
  level: number,
  labelPath: string,
  parentPath: number[],
  levelTotals: { [level: number]: number }
): { levelData: { [level: number]: any[] }; maxLevel: number } => {
  const levelData: { [level: number]: any[] } = {};
  let maxLevel = level;

  // Calculate total for this level
  const levelTotal = data.reduce((sum: number, item: any) => {
    return sum + (getVal(item, "count") || 0);
  }, 0);
  levelTotals[level] = levelTotal;

  // Process items at current level
  const sortedData = [...data].sort((a: any, b: any) => {
    const countA = getVal(a, "count") || 0;
    const countB = getVal(b, "count") || 0;
    return countB - countA;
  });

  levelData[level] = sortedData.map((item: any, index: number) => {
    const count = getVal(item, "count") || 0;
    const name = getVal(item, labelPath || "display");
    const display = Array.isArray(name) ? name[0] : name;
    const currentPath = [...parentPath, index];

    return {
      labels: {
        count,
        percentage: getPercentage(count, levelTotal),
        name: display,
        tooltipLabel: display,
        level,
        parentPath: currentPath,
        index,
      },
    };
  });

  // Recursively process children
  sortedData.forEach((item: any, index: number) => {
    const children = getVal(item, "children") || [];
    if (children.length > 0) {
      const currentPath = [...parentPath, index];
      const childResult = processLevelRecursive(
        children,
        level + 1,
        labelPath,
        currentPath,
        levelTotals
      );
      // Merge child level data
      Object.keys(childResult.levelData).forEach((key) => {
        const levelKey = parseInt(key);
        if (!levelData[levelKey]) {
          levelData[levelKey] = [];
        }
        levelData[levelKey].push(...childResult.levelData[levelKey]);
      });
      maxLevel = Math.max(maxLevel, childResult.maxLevel);
    }
  });

  // Reassign indices to be unique within each level after merging
  Object.keys(levelData).forEach((key) => {
    const levelKey = parseInt(key);
    levelData[levelKey] = levelData[levelKey].map((item: any, idx: number) => ({
      ...item,
      labels: {
        ...item.labels,
        index: idx,
      },
    }));
  });

  return { levelData, maxLevel };
};

export const usePieChartData = ({
  barChartData,
  isBreakdown,
  rawData,
  labelPath,
  valuePath,
}: {
  barChartData: any[];
  isBreakdown: boolean;
  rawData?: any;
  labelPath?: string;
  valuePath?: string | string[];
}): MultiLevelPieData | any[] => {
  return useMemo(() => {
    if (!barChartData || barChartData.length === 0) {
      return [];
    }

    // Check if raw data has children arrays (multi-level structure)
    const hasMultiLevelStructure =
      rawData &&
      Array.isArray(rawData) &&
      rawData.length > 0 &&
      rawData.some((d: any) => d.children && Array.isArray(d.children) && d.children.length > 0);

    if (hasMultiLevelStructure && isBreakdown && Array.isArray(valuePath)) {
      // Process all levels recursively
      const levelTotals: { [level: number]: number } = {};
      const { levelData, maxLevel } = processLevelRecursive(
        rawData,
        0,
        labelPath || "display",
        [],
        levelTotals
      );

      // For backward compatibility, maintain parentData and childDataByParent structure
      const parentData = levelData[0] || [];
      const childDataByParent: { [parentIndex: number]: any[] } = {};

      // Build childDataByParent for level 1 children (backward compatibility)
      if (levelData[1]) {
        parentData.forEach((parent: any, parentIndex: number) => {
          const children = levelData[1].filter((child: any) => 
            child.labels.parentPath.length === 2 && child.labels.parentPath[0] === parentIndex
          );
          childDataByParent[parentIndex] = children.map((child: any) => ({
            ...child,
          labels: {
              ...child.labels,
            parentIndex,
              childIndex: child.labels.parentPath[1],
              },
          }));
          });
      }

      return {
        isMultiLevel: true,
        parentData,
        childDataByParent,
        levelData,
        maxLevel,
        levelColors: {}, // Will be populated in the component
      };
    }

    // Original single-level processing
    if (!isBreakdown) {
      // For non-breakdown data, the structure is already compatible
      return barChartData.map((item: CountVizDatum) => ({
        labels: {
          percentage: item.labels.percentage,
          count: item.labels.count,
          name: item.labels.name,
          tooltipLabel: item.labels.tooltipLabel || item.labels.name,
          ...item.labels,
        },
      }));
    } else {
      // For breakdown data, sum all values for each main category
      return barChartData.map((itemArray: CountVizDatum[]) => {
        const totalCount = itemArray.reduce((sum, item) => sum + item.labels.count, 0);
        const mainItem = itemArray[0]; // Use the first item for the main label
        
        // Calculate total across all items for percentage calculation
        const grandTotal = barChartData.reduce((total, group: CountVizDatum[]) => {
          return total + group.reduce((groupSum, item) => groupSum + item.labels.count, 0);
        }, 0);
        
        return {
          labels: {
            percentage: getPercentage(totalCount, grandTotal),
            count: totalCount,
            name: mainItem.labels.name,
            tooltipLabel: mainItem.labels.tooltipLabel || mainItem.labels.name,
            // A collapsed group is still one dim1 bucket — keep its id so the
            // slice can be drilled into.
            value: (mainItem.labels as any).rowValue ?? (mainItem.labels as any).value,
          },
        };
      });
    }
  }, [barChartData, isBreakdown, rawData, labelPath, valuePath]);
};